import { computed, effect, inject, Injectable, type Signal, signal, untracked } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { map, type Observable } from 'rxjs';

import {
	type ProjectNavigationEntry,
	ProjectsForNavigationService,
	ProjectsRepository,
} from '@efficiently/client-api-projects';
import { ClientLoggerFacadeService } from '@efficiently/client-core-logger';
import type { ProjectsForNavigationResult } from '@efficiently/client-domain-projects';

const READ_FAILED_EVENT = 'projects.navigation_read_failed';
const READ_FAILED_TEXT =
	'The projects list could not be read, so project domains show no project numbers and open their product tabs on their section landings';

const UNKNOWN_ERROR_NAME = 'UnknownError';

function toProjectNavigationEntries(projects: ProjectsForNavigationResult): readonly ProjectNavigationEntry[] {
	return projects.flatMap((project): readonly ProjectNavigationEntry[] =>
		project._id
			? [
					{
						projectId: project._id,
						domainId: project.domainId ?? null,
						name: project.name,
						orgProjectNo: project.orgProjectNumber ?? null,
					},
				]
			: [],
	);
}

function toErrorName(failure: unknown): string {
	return failure instanceof Error ? failure.name : UNKNOWN_ERROR_NAME;
}

@Injectable()
export class DefaultProjectsForNavigationService extends ProjectsForNavigationService {
	private readonly projectsRepository = inject(ProjectsRepository);
	private readonly logger = inject(ClientLoggerFacadeService, { optional: true });

	private readonly isRequested = signal(false);
	private readonly isRereadPending = signal(false);

	// eslint-disable-next-line @angular-eslint/no-experimental -- rxResource is the established data-loading API
	private readonly projectsResource = rxResource<readonly ProjectNavigationEntry[], true | undefined>({
		params: () => (this.isRequested() ? true : undefined),
		stream: (): Observable<readonly ProjectNavigationEntry[]> =>
			this.projectsRepository.projectsForNavigation().pipe(map(toProjectNavigationEntries)),
	});

	private lastReportedFailure: unknown = undefined;

	public override readonly projects: Signal<readonly ProjectNavigationEntry[] | null> = computed(() =>
		this.projectsResource.hasValue() ? this.projectsResource.value() : null,
	);

	public override readonly isLoading: Signal<boolean> = computed(
		() => this.projectsResource.isLoading() || this.isRereadPending(),
	);

	public override readonly hasFailed: Signal<boolean> = computed(() => this.projectsResource.error() !== undefined);

	constructor() {
		super();

		effect(() => {
			const failure = this.projectsResource.error();

			untracked(() => this.recordReadFailure(failure));
		});

		effect(() => {
			if (!this.isRereadPending() || this.projectsResource.isLoading()) {
				return;
			}

			untracked(() => {
				this.isRereadPending.set(false);
				this.projectsResource.reload();
			});
		});
	}

	public override load(): void {
		this.isRequested.set(true);
	}

	public override reload(): void {
		if (!untracked(this.isRequested)) {
			this.isRequested.set(true);

			return;
		}

		if (!this.projectsResource.reload()) {
			this.isRereadPending.set(true);
		}
	}

	private recordReadFailure(failure: unknown): void {
		if (failure === this.lastReportedFailure) {
			return;
		}

		this.lastReportedFailure = failure;

		if (failure === undefined) {
			return;
		}

		this.logger?.warn(READ_FAILED_EVENT, READ_FAILED_TEXT, { errorName: toErrorName(failure) });
	}
}
